import React, { useEffect, useMemo, useState } from "react";

import {
  TFunction,
  createBeefFootprintCalculationSummary,
  createDairyFootprintCalculationSummary,
  createFootprintCalculationSummary,
  createPigletFootprintCalculationSummary,
  createPorkFootprintCalculationSummary,
  formatFractionInput,
  formatNumber,
  isPercentageComplete,
  queryIsLoading,
  useDeleteFootprintingBeefByPrimaryDataId,
  useDeleteFootprintingCropByPrimaryDataId,
  useDeleteFootprintingDairyByPrimaryDataId,
  useDeleteFootprintingPigletByPrimaryDataId,
  useDeleteFootprintingPorkByPrimaryDataId,
  useGetBeefFeedTypes,
  useGetDairyFeedTypes,
  useGetFarmDetails,
  useGetFootprintingBeefFeedInformation,
  useGetFootprintingBeefFootprint,
  useGetFootprintingCalculatedBeefFootprints,
  useGetFootprintingCalculatedCropFootprints,
  useGetFootprintingCalculatedDairyFootprints,
  useGetFootprintingCalculatedFarmFootprints,
  useGetFootprintingCalculatedPigletFootprints,
  useGetFootprintingCalculatedPorkFootprints,
  useGetFootprintingCropFootprint,
  useGetFootprintingDairyFeedInformationByPrimaryDataId,
  useGetFootprintingDairyFootprint,
  useGetFootprintingDairyPrimaryDataById,
  useGetFootprintingFarmFootprint,
  useGetFootprintingPigletFeedInformationByPrimaryDataId,
  useGetFootprintingPigletFootprint,
  useGetFootprintingPorkFeedInformationByPrimaryDataId,
  useGetFootprintingPorkFootprint,
  useGetPigletFeedTypes,
  useGetPorkFeedTypes,
  useGetPracticeCrop,
  useNavigate,
  useTranslation,
} from "utils";

import { ENUM_FOOTPRINT_ASSESSMENT_TYPE } from "consts";

import {
  AlertBanner,
  Button,
  Card,
  CardContentGroup,
  ContextMenu,
  ContextMenuItemProps,
  Divider,
  Flex,
  FormCardButtonAddition,
  Icon,
  IconButton,
  Modal,
  Skeleton,
  TagColour,
  Tooltip,
  Typography,
} from "components";

import { FootprintingBeefFeedProductionDrawer } from "pages/app/footprinting/components/FootprintingBeefFeedProductionDrawer";
import { FootprintingDairyFeedProductionDrawer } from "pages/app/footprinting/components/FootprintingDairyFeedProductionDrawer";
import { FootprintingPigletFeedProductionDrawer } from "pages/app/footprinting/components/FootprintingPigletFeedProductionDrawer";
import { FootprintingPorkFeedProductionDrawer } from "pages/app/footprinting/components/FootprintingPorkFeedProductionDrawer";

interface FootprintingFormButton {
  year: number;
  farmId: string;
  cropId?: string;
  isDairy?: boolean;
  yearIsSubmitted?: boolean;
}

export const FootprintingLoadingState = ({ cardType }: { cardType: ENUM_FOOTPRINT_ASSESSMENT_TYPE }) => {
  const config = {
    showImage: cardType === "crop",
    showCO2Display: cardType === "crop",
  };

  return (
    <Card className="padding-125 width-100 margin-top-100 footprint-button-card">
      <Flex gap="28px" align="center">
        {config.showImage && (
          <div className="crop-image-container">
            <Skeleton type="block" height="48px" width="48px" className="skeleton-rectangular" />
          </div>
        )}

        <Flex className="width-100" vertical gap="8px">
          <div className="skeleton-containers form-title margin-bottom-25">
            <Skeleton type="title" width="180px" className="skeleton-rectangular" />
          </div>
          <div className="skeleton-containers form-description margin-bottom-25">
            <Skeleton type="title" width="240px" className="skeleton-rectangular" />
          </div>
        </Flex>

        {config.showCO2Display && (
          <div className="skeleton-containers co2-display">
            <Skeleton type="block" height="18px" width="60px" className="skeleton-rectangular" />
          </div>
        )}

        <div className="skeleton-containers progress">
          <Skeleton type="block" height="18px" width="40px" className="skeleton-rectangular" />
        </div>

        <div className="skeleton-containers">
          <Skeleton type="block" height="32px" width="80px" className="skeleton-rectangular" />
        </div>
      </Flex>
    </Card>
  );
};

export const getTagProps = (
  t: TFunction,
  isSubmitted: boolean,
  isComplete: boolean,
  farmCardCompletion: boolean,
  preliminaryResult: boolean,
  cardType: ENUM_FOOTPRINT_ASSESSMENT_TYPE,
  completionPct: number
): { text: string; colour: TagColour; tooltip?: string }[] => {
  const tags: { text: string; colour: TagColour; tooltip?: string }[] = [];

  const addTag = (text: string, colour: TagColour, tooltip?: string) => {
    tags.push({ text: t(text), colour, tooltip: tooltip ? t(tooltip) : undefined });
  };

  if (completionPct === 0 && cardType === "farm") {
    addTag("formButtons.tag.startHere", "orange", "formButtons.tooltips.tooltipFarm");
  }

  if (isComplete && !farmCardCompletion) {
    addTag("formButtons.tag.farmDataMissing", "orange", "formButtons.tooltips.tooltipFarmDataMissing");
  }

  if (((isSubmitted && preliminaryResult) || isSubmitted) && cardType === "crop") {
    addTag("formButtons.tag.preliminaryResult", "yellow", "formButtons.tooltips.tooltipPreliminaryResult");
  }

  return tags;
};

interface FootprintingFormButtonProps {
  farmId: string;
  cropId?: string;
  year: number;
  cardType: ENUM_FOOTPRINT_ASSESSMENT_TYPE;
  yearIsSubmitted: boolean;
  initialStep?: number;
  onFormFlowClose?: () => void;
}

const CARD_TITLE: Record<ENUM_FOOTPRINT_ASSESSMENT_TYPE, string> = {
  beef: "formButtons.beefButtonTitle",
  crop: "",
  dairy: "formButtons.dairyButtonTitle",
  farm: "formButtons.farmButtonTitle",
  piglet: "formButtons.pigletButtonTitle",
  pork: "formButtons.porkButtonTitle",
};

export const CARD_TYPE_ICON: Record<Exclude<ENUM_FOOTPRINT_ASSESSMENT_TYPE, "farm">, string> = {
  crop: "klim/crops-matured",
  beef: "tabler/meat",
  dairy: "klim/cow",
  piglet: "tabler/pig",
  pork: "tabler/pig",
};

const FootprintingFormButton = ({ farmId, year, cardType, cropId, yearIsSubmitted, initialStep }: FootprintingFormButtonProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(["footprinting", "dairy"]);
  const farmDetailsQuery = useGetFarmDetails({ farmId });
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [showRemoveAssessmentModal, setShowRemoveAssessmentModal] = useState(false);
  const [selectedFeedTypeId, setSelectedFeedTypeId] = useState<string | null>(null);

  const cropInfo = useGetPracticeCrop(cropId);
  const farmProgressQuery = useGetFootprintingFarmFootprint(farmId, year, { enabled: cardType !== "crop" });
  const beefProgressQuery = useGetFootprintingBeefFootprint(farmId, year, { enabled: cardType === "beef" });
  const cropProgressQuery = useGetFootprintingCropFootprint(farmId, cropId ?? "", year, { enabled: cardType === "crop" && cropId !== undefined });
  const dairyProgressQuery = useGetFootprintingDairyFootprint(farmId, year, { enabled: cardType === "dairy" });
  const dairyPrimaryDataQuery = useGetFootprintingDairyPrimaryDataById(dairyProgressQuery.data?.footprint?.id ?? "", { enabled: cardType === "dairy" });
  const pigletProgressQuery = useGetFootprintingPigletFootprint(farmId, year, { enabled: cardType === "piglet" });
  const porkProgressQuery = useGetFootprintingPorkFootprint(farmId, year, { enabled: cardType === "pork" });
  const dairyFeedTypesQuery = useGetDairyFeedTypes({ enabled: cardType === "dairy" });
  const dairyFeedInformationQuery = useGetFootprintingDairyFeedInformationByPrimaryDataId(dairyProgressQuery.data?.footprint?.id ?? "");
  const beefFeedTypesQuery = useGetBeefFeedTypes({ enabled: cardType === "beef" });
  const beefFeedInformationQuery = useGetFootprintingBeefFeedInformation(beefProgressQuery.data?.footprint?.id ?? "", { enabled: cardType === "beef" });
  const porkFeedInformationQuery = useGetFootprintingPorkFeedInformationByPrimaryDataId(porkProgressQuery.data?.footprint?.id ?? "");
  const porkFeedsQuery = useGetPorkFeedTypes({ enabled: cardType === "pork" });
  const pigletFeedInformationQuery = useGetFootprintingPigletFeedInformationByPrimaryDataId(pigletProgressQuery.data?.footprint?.id ?? "");
  const pigletFeedsQuery = useGetPigletFeedTypes({ enabled: cardType === "piglet" });
  const farmCalculationQueries = useGetFootprintingCalculatedFarmFootprints(farmId, [year], {
    enabled: isPercentageComplete(farmProgressQuery.data?.completionPct ?? 0),
  });
  const cropCalculationQueries = useGetFootprintingCalculatedCropFootprints(farmId, [year], [cropId ?? ""], {
    enabled: cardType === "crop" && isPercentageComplete(cropProgressQuery.data?.completionPct ?? 0),
  });

  const dairyCalculationQueries = useGetFootprintingCalculatedDairyFootprints(farmId, [year], {
    enabled: cardType === "dairy" && isPercentageComplete(dairyProgressQuery.data?.completionPct ?? 0),
  });

  const beefCalculationQueries = useGetFootprintingCalculatedBeefFootprints(farmId, [year], {
    enabled: cardType === "beef" && isPercentageComplete(beefProgressQuery.data?.completionPct ?? 0),
  });

  const pigletCalculationQueries = useGetFootprintingCalculatedPigletFootprints(farmId, [year], {
    enabled: cardType === "piglet" && isPercentageComplete(pigletProgressQuery.data?.completionPct ?? 0),
  });
  const porkCalculationQueries = useGetFootprintingCalculatedPorkFootprints(farmId, [year], {
    enabled: cardType === "pork" && isPercentageComplete(porkProgressQuery.data?.completionPct ?? 0),
  });

  const deleteFootprintingBeefByPrimaryDataId = useDeleteFootprintingBeefByPrimaryDataId(farmId, year, beefProgressQuery.data?.footprint?.id);
  const deleteFootprintingPigletByPrimaryDataId = useDeleteFootprintingPigletByPrimaryDataId(farmId, year, pigletProgressQuery.data?.footprint?.id);
  const deleteFootprintingPorkByPrimaryDataId = useDeleteFootprintingPorkByPrimaryDataId(farmId, year, porkProgressQuery.data?.footprint?.id);
  const deleteFootprintingDairyByPrimaryDataId = useDeleteFootprintingDairyByPrimaryDataId(farmId, year, dairyProgressQuery.data?.footprint?.id);
  const deleteFootprintingCropByPrimaryDataId = useDeleteFootprintingCropByPrimaryDataId(farmId, cropId ?? "", year, cropProgressQuery.data?.footprint?.id);

  const footprintDisplayData = useMemo(() => {
    if (!farmDetailsQuery.data || !farmCalculationQueries[0].data) return null;

    if (cardType === "crop" && cropCalculationQueries[0].data && cropProgressQuery.data?.footprint) {
      return createFootprintCalculationSummary(
        farmDetailsQuery.data,
        farmCalculationQueries[0].data,
        cropCalculationQueries[0].data,
        cropProgressQuery.data.footprint
      );
    }

    if (cardType === "beef" && beefCalculationQueries[0].data && beefProgressQuery.data?.footprint) {
      return createBeefFootprintCalculationSummary(
        farmDetailsQuery.data,
        farmCalculationQueries[0].data,
        beefCalculationQueries[0].data,
        beefProgressQuery.data
      );
    }

    if (cardType === "dairy" && dairyCalculationQueries[0].data && dairyProgressQuery.data?.footprint) {
      return createDairyFootprintCalculationSummary(
        farmDetailsQuery.data,
        farmCalculationQueries[0].data,
        dairyCalculationQueries[0].data,
        dairyProgressQuery.data
      );
    }

    if (cardType === "pork" && porkCalculationQueries[0].data && porkProgressQuery.data?.footprint) {
      return createPorkFootprintCalculationSummary(farmDetailsQuery.data, farmCalculationQueries[0].data, porkCalculationQueries[0].data);
    }

    if (cardType === "piglet" && pigletCalculationQueries[0].data && pigletProgressQuery.data?.footprint) {
      return createPigletFootprintCalculationSummary(farmDetailsQuery.data, farmCalculationQueries[0].data, pigletCalculationQueries[0].data);
    }

    return null;
  }, [
    farmDetailsQuery.data,
    farmCalculationQueries,
    cardType,
    beefCalculationQueries,
    beefProgressQuery.data,
    cropCalculationQueries,
    cropProgressQuery.data?.footprint,
    dairyCalculationQueries,
    dairyProgressQuery.data,
    pigletCalculationQueries,
    pigletProgressQuery.data,
    porkCalculationQueries,
    porkProgressQuery.data,
  ]);

  const dairyFeedTypesWithCompletion = useMemo(() => {
    if (!dairyFeedInformationQuery.data || !dairyFeedTypesQuery.data) {
      return [];
    }

    return dairyFeedInformationQuery.data.flatMap((feedData) => {
      const feedType = dairyFeedTypesQuery.data.find((ft) => ft.id === feedData.feedTypeId);

      if (!feedType || !feedData.isSelfProduced) return [];

      return [
        {
          feedTypeId: feedType.id,
          feedInformationId: feedData.id ?? "",
          name: feedType.name,
          translationSlug: feedType.translationSlug,
          isComplete: dairyProgressQuery.data?.feedProductionCompletion?.[feedData.id ?? ""] ?? false,
        },
      ];
    });
  }, [dairyFeedInformationQuery.data, dairyFeedTypesQuery.data, dairyProgressQuery.data?.feedProductionCompletion]);

  const beefFeedTypesWithCompletion = useMemo(() => {
    if (!beefFeedInformationQuery.data || !beefFeedTypesQuery.data) {
      return [];
    }

    return beefFeedInformationQuery.data.flatMap((feedData) => {
      const feedType = beefFeedTypesQuery.data.find((ft) => ft.id === feedData.feedTypeId);

      if (!feedType || !feedData.isSelfProduced) return [];

      return [
        {
          feedTypeId: feedType.id,
          feedInformationId: feedData.id ?? "",
          name: feedType.name,
          translationSlug: feedType.translationSlug,
          isComplete: beefProgressQuery.data?.feedProductionCompletion?.[feedData.id ?? ""] ?? false,
        },
      ];
    });
  }, [beefFeedInformationQuery.data, beefFeedTypesQuery.data, beefProgressQuery.data?.feedProductionCompletion]);

  const porkFeedTypesWithCompletion = useMemo(() => {
    if (!porkFeedInformationQuery.data || !porkFeedsQuery.data) {
      return [];
    }

    return porkFeedInformationQuery.data.flatMap((feedData) => {
      const feed = porkFeedsQuery.data.find((f) => f.id === feedData.feedId);

      if (!feed || !feedData.isSelfProduced) return [];

      return [
        {
          feedId: feed.id,
          feedInformationId: feedData.id ?? "",
          name: feed.name,
          translationSlug: feed.translationSlug,
          isComplete: porkProgressQuery.data?.feedProductionCompletion?.[feedData.id ?? ""] ?? false,
        },
      ];
    });
  }, [porkFeedInformationQuery.data, porkFeedsQuery.data, porkProgressQuery.data?.feedProductionCompletion]);

  const pigletFeedTypesWithCompletion = useMemo(() => {
    if (!pigletFeedInformationQuery.data || !pigletFeedsQuery.data) {
      return [];
    }

    return pigletFeedInformationQuery.data.flatMap((feedData) => {
      const feed = pigletFeedsQuery.data.find((f) => f.id === feedData.feedId);

      if (!feed || !feedData.isSelfProduced) return [];

      return [
        {
          feedId: feed.id,
          feedInformationId: feedData.id ?? "",
          name: feed.name,
          translationSlug: feed.translationSlug,
          isComplete: pigletProgressQuery.data?.feedProductionCompletion?.[feedData.id ?? ""] ?? false,
        },
      ];
    });
  }, [pigletFeedInformationQuery.data, pigletFeedsQuery.data, pigletProgressQuery.data?.feedProductionCompletion]);

  // Note: We DO NOT care if the ...PrimaryData query is loading
  const loading =
    queryIsLoading(farmDetailsQuery) ||
    (cardType === "crop"
      ? cropCalculationQueries.some(queryIsLoading) || queryIsLoading(cropProgressQuery)
      : cardType === "beef"
      ? queryIsLoading(beefProgressQuery)
      : cardType === "dairy"
      ? queryIsLoading(dairyProgressQuery)
      : cardType === "piglet"
      ? queryIsLoading(pigletProgressQuery)
      : cardType === "pork"
      ? queryIsLoading(porkProgressQuery)
      : queryIsLoading(farmProgressQuery));

  // Open form flow when initialStep is provided
  useEffect(() => {
    if (initialStep !== undefined && cardType === "crop") {
      openFormFlow();
    }
  }, [initialStep]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <FootprintingLoadingState cardType={cardType} />;
  }

  const completionPct =
    (cardType === "crop"
      ? cropProgressQuery?.data?.completionPct
      : cardType === "beef"
      ? beefProgressQuery?.data?.completionPct
      : cardType === "dairy"
      ? dairyProgressQuery?.data?.completionPct
      : cardType === "piglet"
      ? pigletProgressQuery?.data?.completionPct
      : cardType === "pork"
      ? porkProgressQuery?.data?.completionPct
      : farmProgressQuery.data?.completionPct) ?? 0;
  const isComplete = isPercentageComplete(completionPct);
  const farmCardCompletion = (farmProgressQuery.data?.completionPct ?? 0) >= 1;
  const preliminaryResult = !!footprintDisplayData?.overall?.perKgYieldCO2e;
  const isSubmittedAndIncomplete = yearIsSubmitted && cardType === "crop" && !isComplete;
  const isCropFormSubmitted = yearIsSubmitted && cardType === "crop";
  const showDairyFeedAddition = cardType === "dairy" && dairyFeedTypesWithCompletion.length > 0 && !isSubmittedAndIncomplete;
  const showBeefFeedAddition = cardType === "beef" && beefFeedTypesWithCompletion.length > 0 && !isSubmittedAndIncomplete;
  const showPorkFeedAddition = cardType === "pork" && porkFeedTypesWithCompletion.length > 0;
  const showPigletFeedAddition = cardType === "piglet" && pigletFeedTypesWithCompletion.length > 0;
  const selectedDairyFeedType = dairyFeedTypesWithCompletion.find((ft) => ft.feedInformationId === selectedFeedTypeId);
  const selectedBeefFeedType = beefFeedTypesWithCompletion.find((ft) => ft.feedInformationId === selectedFeedTypeId);
  const selectedPorkFeedType = porkFeedTypesWithCompletion.find((ft) => ft.feedInformationId === selectedFeedTypeId);
  const selectedPigletFeedType = pigletFeedTypesWithCompletion.find((ft) => ft.feedInformationId === selectedFeedTypeId);

  const assessmentName = cardType === "crop" ? cropInfo?.name : t(CARD_TITLE[cardType]);

  const openFormFlow = () => {
    navigate("/footprinting/form", { state: { year, formType: cardType, cropId } });
  };

  const deleteAssessment = () => {
    if (cardType === "beef") {
      deleteFootprintingBeefByPrimaryDataId.mutate();
    }

    if (cardType === "crop") {
      deleteFootprintingCropByPrimaryDataId.mutate();
    }

    if (cardType === "dairy") {
      deleteFootprintingDairyByPrimaryDataId.mutate();
    }

    if (cardType === "piglet") {
      deleteFootprintingPigletByPrimaryDataId.mutate();
    }

    if (cardType === "pork") {
      deleteFootprintingPorkByPrimaryDataId.mutate();
    }
  };

  // TODO: This really needs to be cleaned up
  const contextMenuItems: ContextMenuItemProps[] = [
    cardType === "beef" && {
      label: (
        <Tooltip placement="right" title={t("formButtons.editDisabledTooltip")} disabled={!yearIsSubmitted}>
          <Typography variant="paragraph-m">{t("formButtons.button.edit")}</Typography>
        </Tooltip>
      ),
      onClick: () => openFormFlow(),
      state: yearIsSubmitted ? "disabled" : undefined,
    },
    cardType === "beef" &&
      yearIsSubmitted && {
        label: t("formButtons.reviewSubmittedData"),
        onClick: () => openFormFlow(),
      },
    cardType === "beef" &&
      !yearIsSubmitted && {
        label: <Typography variant="paragraph-m">{t("formButtons.button.remove")}</Typography>,
        onClick: () => setShowRemoveAssessmentModal(true),
      },
    cardType === "crop" && {
      label: (
        <Tooltip placement="right" title={t("formButtons.editDisabledTooltip")} disabled={!isCropFormSubmitted}>
          <Typography variant="paragraph-m">{t("formButtons.button.edit")}</Typography>
        </Tooltip>
      ),
      onClick: () => openFormFlow(),
      state: isCropFormSubmitted ? "disabled" : undefined,
    },
    cardType === "crop" &&
      isCropFormSubmitted && {
        label: t("formButtons.reviewSubmittedData"),
        onClick: () => openFormFlow(),
      },
    cardType === "crop" &&
      !isCropFormSubmitted && {
        label: <Typography variant="paragraph-m">{t("formButtons.button.remove")}</Typography>,
        onClick: () => setShowRemoveAssessmentModal(true),
      },
    cardType === "dairy" && {
      label: (
        <Tooltip placement="right" title={t("formButtons.editDisabledTooltip")} disabled={!yearIsSubmitted}>
          <Typography variant="paragraph-m">{t("formButtons.button.edit")}</Typography>
        </Tooltip>
      ),
      onClick: () => openFormFlow(),
      state: yearIsSubmitted ? "disabled" : undefined,
    },
    cardType === "dairy" &&
      yearIsSubmitted && {
        label: t("formButtons.reviewSubmittedData"),
        onClick: () => openFormFlow(),
      },
    cardType === "dairy" &&
      !yearIsSubmitted && {
        label: <Typography variant="paragraph-m">{t("formButtons.button.remove")}</Typography>,
        onClick: () => setShowRemoveAssessmentModal(true),
      },
    cardType === "piglet" && {
      label: (
        <Tooltip placement="right" title={t("formButtons.editDisabledTooltip")} disabled={!yearIsSubmitted}>
          <Typography variant="paragraph-m">{t("formButtons.button.edit")}</Typography>
        </Tooltip>
      ),
      onClick: () => openFormFlow(),
      state: yearIsSubmitted ? "disabled" : undefined,
    },
    cardType === "piglet" &&
      yearIsSubmitted && {
        label: t("formButtons.reviewSubmittedData"),
        onClick: () => openFormFlow(),
      },
    cardType === "piglet" &&
      !yearIsSubmitted && {
        label: <Typography variant="paragraph-m">{t("formButtons.button.remove")}</Typography>,
        onClick: () => setShowRemoveAssessmentModal(true),
      },
    cardType === "pork" && {
      label: (
        <Tooltip placement="right" title={t("formButtons.editDisabledTooltip")} disabled={!yearIsSubmitted}>
          <Typography variant="paragraph-m">{t("formButtons.button.edit")}</Typography>
        </Tooltip>
      ),
      onClick: () => openFormFlow(),
      state: yearIsSubmitted ? "disabled" : undefined,
    },
    cardType === "pork" &&
      yearIsSubmitted && {
        label: t("formButtons.reviewSubmittedData"),
        onClick: () => openFormFlow(),
      },
    cardType === "pork" &&
      !yearIsSubmitted && {
        label: <Typography variant="paragraph-m">{t("formButtons.button.remove")}</Typography>,
        onClick: () => setShowRemoveAssessmentModal(true),
      },
  ].filter(Boolean) as ContextMenuItemProps[];

  const showContextMenuButton = contextMenuItems.length > 0;

  const handleButtonClick = () => {
    const isSubmittedViewResultsButton = completionPct >= 1 && yearIsSubmitted && cardType !== "farm";
    const isUnsubmittedViewResultsButton = !yearIsSubmitted && cardType !== "farm" && completionPct >= 1 && farmCardCompletion;

    if (isSubmittedViewResultsButton || isUnsubmittedViewResultsButton) {
      // TODO: we will need to rethink how we do results ID tracking vis a vis passing `footprintingId` is now ambigious dairy vs pork
      navigate("/footprinting/results", {
        state: {
          footprintId:
            cardType === "crop"
              ? cropId
              : cardType === "pork"
              ? porkProgressQuery.data?.footprint?.id
              : cardType === "piglet"
              ? pigletProgressQuery.data?.footprint?.id
              : cardType === "beef"
              ? beefProgressQuery.data?.footprint?.id
              : dairyPrimaryDataQuery.data?.id,
          year,
        },
      });
    } else {
      openFormFlow();
    }
  };

  const cardContent = (
    <Card
      className={`padding-100 width-100 margin-top-100 footprint-button-card ${isSubmittedAndIncomplete ? "disabled-footprinting-form-button" : ""} ${
        showDairyFeedAddition || showBeefFeedAddition || showPorkFeedAddition || showPigletFeedAddition ? "has-addition" : ""
      }`}
      onClick={handleButtonClick}
    >
      <Flex align="center">
        {cardType !== "farm" ? (
          <div className="crop-image-container margin-right-125">
            <Icon
              iconName={CARD_TYPE_ICON[cardType]}
              alt={
                cardType === "crop"
                  ? `${cropInfo?.name ?? "Crop"} icon`
                  : cardType === "beef"
                  ? "beef icon"
                  : cardType === "dairy"
                  ? "dairy icon"
                  : cardType === "piglet"
                  ? "piglet icon"
                  : "pork icon"
              }
              size="m"
            />
          </div>
        ) : null}
        <CardContentGroup
          grow
          title={cropInfo?.name ?? t(CARD_TITLE[cardType])}
          description={cardType === "farm" ? t("formButtons.farmButtonDescription") : ""}
          tag={getTagProps(t, yearIsSubmitted ?? false, isComplete, farmCardCompletion, preliminaryResult, cardType, completionPct)}
        />
        {cardType !== "farm" ? (
          <CardContentGroup
            align="right"
            title={isComplete && footprintDisplayData ? `${formatNumber({ number: footprintDisplayData.overall.perKgYieldCO2e, decimalPlaces: 2 })} kg` : "-"}
            loading={
              cardType === "crop"
                ? cropCalculationQueries[0].isFetching
                : cardType === "beef"
                ? beefCalculationQueries[0].isFetching
                : cardType === "pork"
                ? porkCalculationQueries[0].isFetching
                : cardType === "piglet"
                ? pigletCalculationQueries[0].isFetching
                : dairyCalculationQueries[0].isFetching
            }
            description={t("formButtons.totalCo2PerKgYield")}
            tooltipTitle={
              cardType === "crop" ? (yearIsSubmitted ? t("formButtons.submittedTotalCo2Tooltip") : t("formButtons.inProgressTotalCo2Tooltip")) : undefined
            }
          />
        ) : null}
        <div className="margin-left-250 margin-right-100">
          <CardContentGroup align="right" title={`${Math.round(formatFractionInput(completionPct) ?? 0)}%`} description={t("formButtons.completePct")} />
        </div>
        <div className="footprint-button-card-column center margin-left-400">
          <Flex gap={20} align="center">
            <Tooltip
              disabled={!isSubmittedAndIncomplete && !(completionPct >= 1 && yearIsSubmitted)}
              placement="top"
              title={isSubmittedAndIncomplete ? t("formButtons.submittedAndIncompleteTooltip") : undefined}
            >
              <Button
                style={{ width: "fit-content" }}
                type="secondary"
                size="small"
                onClick={handleButtonClick}
                disabled={(completionPct >= 1 && yearIsSubmitted && cardType === "farm") || isSubmittedAndIncomplete}
              >
                {t(
                  yearIsSubmitted && cardType !== "farm"
                    ? "formButtons.button.viewResults"
                    : completionPct < 0.01
                    ? "formButtons.button.start"
                    : completionPct <= 0.99
                    ? "formButtons.button.continue"
                    : yearIsSubmitted
                    ? "formButtons.button.reviewData"
                    : cardType !== "farm" && farmCardCompletion
                    ? "formButtons.button.viewResults"
                    : "formButtons.button.edit"
                )}
              </Button>
            </Tooltip>
          </Flex>
        </div>
        <Flex className="context-menu-container margin-left-100 margin-right-100">
          {showContextMenuButton ? (
            <ContextMenu
              items={contextMenuItems}
              placement="bottom-right"
              visible={showContextMenu}
              onClose={() => setShowContextMenu(false)}
              dataTestId="footprinting-crop-context-menu"
              className="footprinting-context-menu"
              iconPlacement="center"
            >
              <Flex
                data-testid="footprinting-crop-context-menu-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowContextMenu(true);
                }}
              >
                <IconButton type="tertiary" size="small" iconName="tabler/dots-vertical" className="pointer" />
              </Flex>
            </ContextMenu>
          ) : (
            <div className="context-menu-placeholder" />
          )}
        </Flex>
      </Flex>
    </Card>
  );

  const additionCard = showDairyFeedAddition && (
    <Card className="footprint-button-card-addition">
      <FormCardButtonAddition
        items={dairyFeedTypesWithCompletion.map((feedType) => ({
          id: feedType.feedInformationId,
          name: feedType.name,
          icon: "🌱",
          isComplete: feedType.isComplete,
        }))}
        completeText={t("dairyFormButtonAdditionTag.complete")}
        incompleteText={t("dairyFormButtonAdditionTag.incomplete")}
        completeColor="green"
        incompleteColor="grey"
        onItemClick={(feedInformationId) => setSelectedFeedTypeId(feedInformationId)}
      />
    </Card>
  );

  const beefAdditionCard = showBeefFeedAddition && (
    <Card className="footprint-button-card-addition">
      <FormCardButtonAddition
        items={beefFeedTypesWithCompletion.map((feedType) => ({
          id: feedType.feedInformationId,
          name: feedType.name,
          icon: "🌱",
          isComplete: feedType.isComplete,
        }))}
        completeText={t("dairyFormButtonAdditionTag.complete")}
        incompleteText={t("dairyFormButtonAdditionTag.incomplete")}
        completeColor="green"
        incompleteColor="grey"
        onItemClick={(feedInformationId) => setSelectedFeedTypeId(feedInformationId)}
      />
    </Card>
  );

  const porkAdditionCard = showPorkFeedAddition && (
    <Card className="footprint-button-card-addition">
      <FormCardButtonAddition
        items={porkFeedTypesWithCompletion.map((feedType) => ({
          id: feedType.feedInformationId,
          name: feedType.name,
          icon: "🌱",
          isComplete: feedType.isComplete,
        }))}
        completeText={t("dairyFormButtonAdditionTag.complete")}
        incompleteText={t("dairyFormButtonAdditionTag.incomplete")}
        completeColor="green"
        incompleteColor="grey"
        onItemClick={(feedInformationId) => setSelectedFeedTypeId(feedInformationId)}
      />
    </Card>
  );

  const pigletAdditionCard = showPigletFeedAddition && (
    <Card className="footprint-button-card-addition">
      <FormCardButtonAddition
        items={pigletFeedTypesWithCompletion.map((feedType) => ({
          id: feedType.feedInformationId,
          name: feedType.name,
          icon: "🌱",
          isComplete: feedType.isComplete,
        }))}
        completeText={t("dairyFormButtonAdditionTag.complete")}
        incompleteText={t("dairyFormButtonAdditionTag.incomplete")}
        completeColor="green"
        incompleteColor="grey"
        onItemClick={(feedInformationId) => setSelectedFeedTypeId(feedInformationId)}
      />
    </Card>
  );

  return (
    <>
      {showDairyFeedAddition || showBeefFeedAddition || showPorkFeedAddition || showPigletFeedAddition ? (
        <div className="footprint-button-card-wrapper">
          {cardContent}
          {additionCard}
          {beefAdditionCard}
          {porkAdditionCard}
          {pigletAdditionCard}
        </div>
      ) : (
        cardContent
      )}

      {selectedDairyFeedType && (
        <FootprintingDairyFeedProductionDrawer
          farmId={farmId}
          year={year}
          visible={selectedFeedTypeId !== null}
          onClose={() => setSelectedFeedTypeId(null)}
          feedInformationId={selectedDairyFeedType.feedInformationId}
          feedTypeName={selectedDairyFeedType.name}
          primaryDataId={dairyProgressQuery.data?.footprint?.id}
        />
      )}

      {selectedBeefFeedType && (
        <FootprintingBeefFeedProductionDrawer
          farmId={farmId}
          year={year}
          visible={selectedFeedTypeId !== null}
          onClose={() => setSelectedFeedTypeId(null)}
          feedInformationId={selectedBeefFeedType.feedInformationId}
          feedTypeName={selectedBeefFeedType.name}
          primaryDataId={beefProgressQuery.data?.footprint?.id}
        />
      )}

      {selectedPorkFeedType && (
        <FootprintingPorkFeedProductionDrawer
          farmId={farmId}
          year={year}
          visible={selectedFeedTypeId !== null}
          onClose={() => setSelectedFeedTypeId(null)}
          feedInformationId={selectedPorkFeedType.feedInformationId}
          primaryDataId={porkProgressQuery.data?.footprint?.id}
          feedTypeName={selectedPorkFeedType.name}
        />
      )}

      {selectedPigletFeedType && (
        <FootprintingPigletFeedProductionDrawer
          farmId={farmId}
          year={year}
          visible={selectedFeedTypeId !== null}
          onClose={() => setSelectedFeedTypeId(null)}
          feedInformationId={selectedPigletFeedType.feedInformationId}
          primaryDataId={pigletProgressQuery.data?.footprint?.id}
          feedTypeName={selectedPigletFeedType.name}
        />
      )}

      <Modal
        showModal={showRemoveAssessmentModal}
        closeIcon={<Icon className="grey" iconName="tabler/x" />}
        onCancel={() => setShowRemoveAssessmentModal(false)}
        onOk={() => deleteAssessment()}
        okText={t("formButtons.button.remove")}
        cancelText={t("modal.cancel")}
        title={t("assessmentManagement.removeAssessmentModal.title", { cropName: assessmentName, year })}
        content={
          <Flex vertical>
            <Typography variant="paragraph-m">{t("assessmentManagement.removeAssessmentModal.description", { cropName: assessmentName, year })}</Typography>
            <AlertBanner
              headingSize="h6"
              type="warning"
              iconSize={24}
              iconAlign="start"
              iconName="tabler/info-circle"
              description={t("assessmentManagement.removeAssessmentModal.banner")}
            />
            <Divider />
            <Typography variant="paragraph-m">{t("assessmentManagement.removeAssessmentModal.info")}</Typography>
          </Flex>
        }
      />
    </>
  );
};

export default FootprintingFormButton;
